/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, memo, Suspense, useEffect } from 'react';
import { registry } from '@/lib/registry';
import { getNormalizedTypeId, normalizeComponentData } from '@/lib/util/pd-utils';
import { Await, useAsyncError } from 'react-router';
import { createLogger } from '@/lib/logger';

const logger = createLogger();
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { useComponentDataById } from './component-data-context';
import type { ComponentType } from './index';

export interface ComponentProps {
    component: ComponentType;
    className?: string;
    regionId: string;
}

/**
 * Error handler component that logs component data loading errors and renders nothing.
 * Uses React Router's useAsyncError to access the error from the Await boundary.
 *
 * When a component's data fails to load (e.g., API error), we render nothing (null)
 * instead of showing a misleading skeleton/fallback. The error is logged to the console
 * for debugging purposes.
 */
function ComponentErrorFallback({ componentId, componentTypeId }: { componentId: string; componentTypeId: string }) {
    const error = useAsyncError();

    useEffect(() => {
        logger.error(`Failed to load data for component "${componentId}" (${componentTypeId})`, { error });
    }, [componentId, componentTypeId, error]);

    // Render nothing when data loading fails
    return null;
}

function normalizeComponent(component: ComponentType): { typeId: string; data: any } {
    const typeId = getNormalizedTypeId(component.typeId);
    const data = normalizeComponentData(component.typeId, component.data);
    return { typeId, data };
}

export const Component = memo(function Component({ component, className, regionId }: ComponentProps): ReactElement {
    // Normalize component typeId and data props
    const { typeId, data: normalizedData } = normalizeComponent(component);

    // Get this component's data promise from context by its ID
    const dataPromise = useComponentDataById(component.id);

    // Check if the component is registered in our storefront registry
    if (!registry.has(typeId)) {
        logger.warn(`Component type "${component.typeId}" (resolved to "${typeId}") is not registered in the storefront registry.`);
        
        // In design mode, render a helpful placeholder box.
        // In runtime, return null/empty to degrade gracefully.
        if (typeof window !== 'undefined' && window.location.search.includes('mode=')) {
            return (
                <div style={{
                    padding: '16px',
                    border: '1px dashed #ef4444',
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    fontFamily: 'sans-serif',
                    fontSize: '14px',
                    margin: '8px 0',
                    borderRadius: '4px'
                }}>
                    <strong>[Not Implemented]</strong> Component type <code>{component.typeId}</code> is not registered in the storefront registry.
                </div>
            );
        }
        return <></>;
    }

    const FallbackComponent = registry.getFallback(typeId);
    const DynamicComponent = registry.getComponent(typeId);
    if (!DynamicComponent) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw registry.preload(typeId);
    }

    const designMetadata: ComponentDesignMetadata = {
        name: component.designMetadata?.name,
        isFragment: Boolean(component.fragment),
        isVisible: Boolean(component.visible),
        isLocalized: Boolean(component.localized),
        id: component.id,
        contentLinkUuid: component.contentLinkUuid,
    };

    return (
        <Suspense fallback={FallbackComponent ? <FallbackComponent {...(normalizedData ?? {})} /> : <div />}>
            <Await
                resolve={dataPromise}
                errorElement={<ComponentErrorFallback componentId={component.id} componentTypeId={typeId} />}>
                {(data) => {
                    let finalData = data;
                    if (component.typeId === 'assets.categorytile' && data) {
                        finalData = {
                            ...data,
                            name: normalizedData.text_headline || data.name,
                            image: normalizedData.image?.url || data.image,
                        };
                    }
                    return (
                        <DynamicComponent
                            {...(normalizedData ?? {})}
                            designMetadata={designMetadata}
                            component={{ ...component, typeId, data: normalizedData }}
                            data={finalData}
                            className={className}
                            regionId={regionId}
                        />
                    );
                }}
            </Await>
        </Suspense>
    );
});
